import { inject, Injectable } from '@angular/core';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TellentComponentsNotificationService } from '@tellent/common-util';
import {
  TellentCurrentUserEntityActions,
  TellentCurrentUserSelectors,
} from '@tellent/current-user-data';
import { TELLENT_CURRENT_USER } from '@tellent/types';
import { filter, map, of, switchMap, tap, withLatestFrom } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { I18nService } from '@recruitee/i18n';
import { createEffectFactory } from '@recruitee/ngrx';

import { resolveMemberFullName } from './../helpers';
import { TellentMembersHttpClient } from './tellent-members.http';
import { TellentMembersActions } from './tellent-members.store';

@Injectable({ providedIn: 'root' })
export class TellentMembersEffects {
  public store = inject(Store);
  public effects = createEffectFactory(TellentMembersActions, this.actions$);

  public inviteTellentMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.inviteTellentMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.inviteTellentMember(payload).pipe(
          map(data => TellentMembersActions.inviteTellentMemberSuccess({ payload: data })),
          catchError(error => {
            this.handleAddMembersFailure(error);
            return of(TellentMembersActions.inviteTellentMemberFail({ payload: error }));
          }),
        ),
      ),
    ),
  );

  public inviteAppMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.inviteAppMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.inviteAppMember(payload).pipe(
          map(data => TellentMembersActions.inviteAppMemberSuccess({ payload: data })),
          catchError(error => {
            this.handleAddMembersFailure(error);
            return of(TellentMembersActions.inviteAppMemberFail({ payload: error }));
          }),
        ),
      ),
    ),
  );

  public transferOwnership$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.transferOwnership),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.transferOwnership(payload).pipe(
          map(data => TellentMembersActions.transferOwnershipSuccess({ payload: data })),
          tap(() => {
            this.store.dispatch(TellentCurrentUserEntityActions.fetch());
            this.notificationService.success(
              this.i18n.translate('tl.team_members.transfer_ownership.success'),
            );
          }),
          catchError(error => of(TellentMembersActions.transferOwnershipFail({ payload: error }))),
        ),
      ),
    ),
  );

  public fetchMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.fetchOne),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.fetchMemberByGuid(payload.id).pipe(
          map(response => TellentMembersActions.fetchOneSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.fetchOneFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public fetchMembers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.fetchPaginatedCollection),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.fetchMembers(payload.params).pipe(
          map(({ data, meta }) =>
            TellentMembersActions.fetchPaginatedCollectionSuccess({
              payload: { data: { rows: data, meta }, scope: payload.params.scope },
            }),
          ),
          catchError(error =>
            of(TellentMembersActions.fetchPaginatedCollectionFailed({ payload: error })),
          ),
        ),
      ),
    ),
  );

  public deactivateMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.deactivateMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.deactivateMember(payload).pipe(
          tap(payload => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.success_toast.deactivate', {
                member: resolveMemberFullName(payload.data),
              }),
            );
          }),
          map(response => TellentMembersActions.deactivateMemberSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.deactivateMemberFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public deleteMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.deleteMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.deleteMember(payload).pipe(
          map(response => TellentMembersActions.deleteMemberSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.deactivateMemberFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public reactivateMember$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.reactivateMember),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.reactivateMember(payload).pipe(
          tap(payload => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.success_toast.reactivate', {
                member: resolveMemberFullName(payload.data),
              }),
            );
          }),
          map(response => TellentMembersActions.reactivateMemberSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.reactivateMemberFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public sendInvitation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.sendInvitation),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.sendInvitation(payload).pipe(
          tap(() => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.success_toast.invite'),
            );
          }),
          map(() => TellentMembersActions.sendInvitationSuccess({ payload })),
          catchError(error => of(TellentMembersActions.sendInvitationFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public manageSeats$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.manageSeats),
      mergeMap(({ payload }) =>
        this.tellentMembersHttpClient.manageSeats(payload).pipe(
          tap(() => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.add_member.roles.success_message'),
            );
          }),
          map(response => TellentMembersActions.manageSeatsSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.manageSeatsFailed({ payload: error }))),
        ),
      ),
    ),
  );

  public updateMemberData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.updateMember),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.updateMemberData(payload).pipe(
          map(response =>
            TellentMembersActions.updateMemberSuccess({
              payload: response,
            }),
          ),
          catchError(error => of(TellentMembersActions.updateMemberFail({ payload: error }))),
        ),
      ),
    ),
  );

  public sendPasswordReset$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.sendPasswordReset),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.sendPasswordResetInstructions(payload.guid).pipe(
          tap(() => {
            this.notificationService.success(
              this.i18n.translate(
                'tlc.members.edit_member.account_tab.section.password.success_toast',
              ),
            );
          }),
          map(response => TellentMembersActions.sendPasswordResetSuccess()),
          catchError(error => of(TellentMembersActions.sendPasswordResetFail({ payload: error }))),
        ),
      ),
    ),
  );

  public updateAbilities$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.updateAbilities),
      switchMap(({ payload }) =>
        this.tellentMembersHttpClient.updateAbilities(payload.guid, payload.abilities).pipe(
          tap(() => {
            this.notificationService.success(
              this.i18n.translate('tlc.members.edit_member.permission_tab.success_toast'),
            );
          }),
          map(response => TellentMembersActions.updateAbilitiesSuccess({ payload: response })),
          catchError(error => of(TellentMembersActions.updateAbilitiesFail({ payload: error }))),
        ),
      ),
    ),
  );

  public refreshCurrentUserOnSeatChange$ = createEffect(() =>
    this.actions$.pipe(
      ofType(TellentMembersActions.manageSeatsSuccess),
      withLatestFrom(
        this.store.select(TellentCurrentUserSelectors.currentOrganizationMembershipSelectorSafe),
      ),
      filter(
        ([action, me]) =>
          !!action.payload.data.find(seat => me?.seats.find(({ guid }) => guid === seat.guid)),
      ),
      switchMap(() => [TellentCurrentUserEntityActions.fetch()]),
    ),
  );

  constructor(
    private tellentMembersHttpClient: TellentMembersHttpClient,
    private actions$: Actions,
    private notificationService: TellentComponentsNotificationService,
    private i18n: I18nService,
  ) {}

  public ngrxOnInitEffects() {
    return TellentMembersActions.fetchCollectionSuccess({
      payload: { data: inject(TELLENT_CURRENT_USER).members || [] },
    });
  }
  private handleAddMembersFailure(error: any) {
    const errorsArray = error.error.errors;
    const getErrorMessage = errorsArray[0].detail ? errorsArray[0].detail : errorsArray[0];

    this.notificationService.error(
      getErrorMessage
        ? getErrorMessage
        : this.i18n.translate('tl.common.errors.error_something_went_wrong'),
    );
  }
}
